import { useMemo, useState } from 'react'
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import { IconBan, IconCheck, IconChecks, IconEye } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLeaveRequests, useConfirmLeaveRequest, useApproveLeaveRequest, useRejectLeaveRequest } from '../../hooks/useLeaveRequests';
import { useEmployees } from '../../hooks/useEmployees';
import ViewLeaveDetailsModal from './components/ViewLeaveDetailsModal';
import ActionDropdown from '../../components/common/ActionDropdown';

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];

export default function LeaveApprovals() {
    const { user } = useAuth();
    const orgId = user?.orgId;

    // Fetch Data
    const { data: leaveRequestsData, isLoading: isLoadingRequests } = useLeaveRequests(orgId);
    const { data: employees } = useEmployees(orgId);

    // Find current employee (the line manager viewing this page)
    const currentEmployee = useMemo(() => {
        return employees?.find(emp =>
            (emp.user && (emp.user === user?.id || emp.user?.id === user?.id)) ||
            (emp.email && emp.email === user?.email) ||
            (emp.work_email && emp.work_email === user?.email)
        );
    }, [employees, user]);

    // Mutations
    const confirmLeaveRequest = useConfirmLeaveRequest();
    const approveLeaveRequest = useApproveLeaveRequest();
    const rejectLeaveRequest = useRejectLeaveRequest();

    // Check if user is HR
    const isHR = user?.is_superuser || user?.permissions?.includes('rbacapp.hr_act') || user?.is_staff;

    const leaveRequests = leaveRequestsData || [];

    const [actionModal, setActionModal] = useState({ isOpen: false, type: '', request: null });
    const [viewDetailsModal, setViewDetailsModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [actionComments, setActionComments] = useState('');
    // Action dropdown state is self-contained

    // Filter states
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle] = useState(false);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const map = {
            pending: <span className='badge badge-warning'>Pending Confirmation</span>,
            confirmed: <span className='badge badge-info'>Confirmed - Awaiting HR</span>,
            approved: <span className='badge badge-success'>Approved</span>,
            rejected: <span className='badge badge-danger'>Rejected</span>,
        };
        return map[status] || <span className='badge badge-secondary'>{status}</span>;
    };

    // --- ACTIONS ---
    const handleActionSubmit = () => {
        const { type, request } = actionModal;
        if (!request) return;

        const options = {
            onSuccess: () => {
                setActionModal({ isOpen: false, type: '', request: null });
                setActionComments('');
                toast.success(`Leave request ${type}ed successfully`);
            },
            onError: (err) => toast.error(`Error: ${err.message}`)
        };

        if (type === 'confirm') {
            const confirmPayload = {
                leave_type: request.leave_type,
                start_date: request.start_date,
                end_date: request.end_date,
                days_requested: request.days_requested
            };

            confirmLeaveRequest.mutate({
                id: request.id,
                orgId,
                data: confirmPayload
            }, options);
        } else if (type === 'approve') {
            approveLeaveRequest.mutate({ id: request.id, orgId }, options);
        } else if (type === 'reject') {
            if (!actionComments.trim()) {
                toast.error('Please provide a reason for rejection');
                return;
            }
            rejectLeaveRequest.mutate({
                id: request.id,
                orgId,
                data: { rejection_reason: actionComments }
            }, options);
        }
    };

    const openActionModal = (type, request) => {
        setActionModal({ isOpen: true, type, request });
        setActionComments('');
    };

    // Table Columns
    const columns = [
        {
            name: 'Employee',
            selector: row => row.employeeName,
            sortable: true,
            cell: row => {
                const initials = row.employeeName
                    ? row.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : '?';
                const bg = AVATAR_COLORS[(row.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
                return (
                    <div className='flex items-center gap-10'>
                        <div style={{ backgroundColor: bg }} className='w-[35px] h-[35px] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0'>
                            {initials}
                        </div>
                        <div>
                            <p className='font-medium text-sm'>{row.employeeName}</p>
                            <p className='text-xs text-text-secondary'>{row.employeeId}</p>
                        </div>
                    </div>
                );
            },
            width: "200px"
        },
        { name: 'Leave Type', selector: row => row.leaveTypeName, sortable: true, width: "150px" },
        { name: 'Duration', selector: row => `${row.days_requested} Days`, sortable: true, width: "100px", center: true },
        { name: 'Dates', selector: row => `${formatDate(row.start_date)} - ${formatDate(row.end_date)}`, width: "200px" },
        { name: 'Status', selector: row => row.status, format: row => getStatusBadge(row.status), sortable: true, width: "180px" },
        {
            name: 'Actions',
            cell: row => {
                const isPending = row.status === 'pending';
                const isConfirmed = row.status === 'confirmed';
                const isDirectLineManager = row.line_manager_id && String(row.line_manager_id) === String(currentEmployee?.id);
                const isCrossOrgManager = !row.line_manager_id && !isHR;
                const isLineManager = isDirectLineManager || isCrossOrgManager;

                // Line Manager can confirm/reject pending requests
                const canConfirm = isPending && isLineManager;
                const canRejectAsManager = isPending && isLineManager;

                // HR can approve/reject confirmed requests
                const canApprove = isConfirmed && isHR;
                const canRejectAsHR = isConfirmed && isHR;

                return (
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'View Details',
                                icon: IconEye,
                                onClick: () => { setSelectedLeave(row); setViewDetailsModal(true); },
                                description: "Review full leave application details"
                            },
                            ...(canConfirm ? [{
                                label: 'Confirm (Manager)',
                                icon: IconCheck,
                                onClick: () => openActionModal('confirm', row),
                                description: "Approve as line manager"
                            }] : []),
                            ...(canApprove ? [{
                                label: 'Approve (HR)',
                                icon: IconChecks,
                                onClick: () => openActionModal('approve', row),
                                description: "Finalize and approve as HR"
                            }] : []),
                            ...((canRejectAsManager || canRejectAsHR) ? [{
                                label: 'Reject Request',
                                icon: IconBan,
                                danger: true,
                                onClick: () => openActionModal('reject', row),
                                description: "Deny this leave request"
                            }] : [])
                        ]}
                    />
                );
            },
            width: "150px",
            center: true
        }
    ];

    // Format and filter data - show requests based on role
    // Line Manager: sees 'pending' requests from their direct reports
    // HR: sees 'confirmed' requests (after line manager approved)
    const formattedLeaves = useMemo(() => {
        return leaveRequests
            .filter(leave => {
                const isDirectLineManager = leave.line_manager_id && String(leave.line_manager_id) === String(currentEmployee?.id);
                // Cross-org: backend already scoped the response to this manager, trust it
                const isCrossOrgManager = !leave.line_manager_id && !isHR;

                // Line Manager (direct or cross-org) sees pending requests
                if ((isDirectLineManager || isCrossOrgManager) && leave.status === 'pending') {
                    return true;
                }

                // HR sees confirmed requests (awaiting HR approval)
                if (isHR && leave.status === 'confirmed') {
                    return true;
                }

                return false;
            })
            .map(leave => {
                // Resolve Employee Name
                let employeeObj = null;
                if (employees && employees.length > 0) {
                    const empId = typeof leave.employee === 'object' ? leave.employee.id : leave.employee;
                    employeeObj = employees.find(e => e.id === empId);
                }

                const employeeName = employeeObj
                    ? (employeeObj.name || `${employeeObj.first_name} ${employeeObj.last_name}`)
                    : (leave.employee_name || 'Unknown User');

                const employeeId = employeeObj
                    ? (employeeObj.employeeId || employeeObj.email)
                    : (leave.employee_id || '');

                const leaveTypeName = leave.leave_type_name || leave.leaveName || 'Unknown Type';

                return {
                    ...leave,
                    employeeName,
                    employeeId,
                    leaveTypeName,
                };
            });
    }, [leaveRequests, employees, currentEmployee, isHR]);

    // Filter Logic
    const filteredItems = formattedLeaves.filter(item => {
        if (!filterText) return true;
        const search = filterText.toLowerCase();
        const getStr = (val) => String(val || '').toLowerCase();

        return (
            getStr(item.leaveTypeName).includes(search) ||
            getStr(item.employeeName).includes(search) ||
            getStr(item.status).includes(search)
        );
    });

    // Count of pending approvals by type
    const managerPendingCount = formattedLeaves.filter(l => l.status === 'pending').length;
    const hrPendingCount = formattedLeaves.filter(l => l.status === 'confirmed').length;
    const totalPendingCount = formattedLeaves.length;

    // Subheader
    const subHeaderComponent = useMemo(() => (
        <div className='flex gap-5 mb-10 w-full justify-end'>
            <input
                type='text'
                placeholder='Search...'
                className='form-control w-full max-w-[250px]'
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText]);

    if (isLoadingRequests) return <div className='p-20 text-center'>Loading...</div>;

    return (
        <div className='flex flex-col min-h-screen gap-20'>
            <Breadcrumb title="Leave Approvals" path="Approvals / Leave Requests" />

            {/* Summary Cards */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-20 mb-30'>
                <div className='bg-bg-dark p-20 rounded shadow-sm border border-border'>
                    <p className='text-xs text-text-secondary uppercase font-bold mb-5'>Total Pending</p>
                    <h3 className='text-3xl font-bold text-org-blue-primary'>{totalPendingCount}</h3>
                    <p className='text-sm text-text-secondary mt-5'>Leave requests awaiting action</p>
                </div>
                {managerPendingCount > 0 && (
                    <div className='bg-bg-dark p-20 rounded shadow-sm border border-border'>
                        <p className='text-xs text-text-secondary uppercase font-bold mb-5'>Manager Confirmation</p>
                        <h3 className='text-3xl font-bold text-warning'>{managerPendingCount}</h3>
                        <p className='text-sm text-text-secondary mt-5'>Awaiting your confirmation</p>
                    </div>
                )}
                {hrPendingCount > 0 && (
                    <div className='bg-bg-dark p-20 rounded shadow-sm border border-border'>
                        <p className='text-xs text-text-secondary uppercase font-bold mb-5'>HR Approval</p>
                        <h3 className='text-3xl font-bold text-success'>{hrPendingCount}</h3>
                        <p className='text-sm text-text-secondary mt-5'>Awaiting HR approval</p>
                    </div>
                )}
            </div>

            {/* MAIN TABLE */}
            <div className='bg-bg-dark rounded shadow-sm p-20 border border-border'>
                <div className='flex justify-between items-center mb-20'>
                    <h2 className='text-lg font-bold'>Leave Requests Pending Approval</h2>
                </div>

                {totalPendingCount === 0 ? (
                    <div className='text-center py-40 text-text-secondary'>
                        <p className='text-lg'>No pending leave requests</p>
                        <p className='text-sm mt-5'>You don't have any leave requests awaiting your action.</p>
                    </div>
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        persistTableHead
                    />
                )}
            </div>

            {/* Action Modal (Confirm/Reject) */}
            {actionModal.isOpen && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
                    <div className='bg-bg-dark border border-border rounded-lg shadow-lg w-full max-w-md p-20 animate-fade-in'>
                        <h3 className='text-lg font-bold mb-10 capitalize text-text-primary'>{actionModal.type} Request</h3>
                        <p className='text-sm text-text-secondary mb-20'>
                            Are you sure you want to {actionModal.type} the leave request for
                            <span className='font-bold text-text-primary'> {actionModal.request?.employeeName}</span>?
                        </p>

                        {actionModal.type === 'reject' && (
                            <textarea
                                className='w-full border border-border bg-bg-light text-text-primary rounded p-10 text-sm mb-20 focus:border-org-blue-primary outline-none'
                                rows={3}
                                placeholder="Reason for rejection..."
                                value={actionComments}
                                onChange={e => setActionComments(e.target.value)}
                            />
                        )}

                        <div className='flex justify-end gap-10'>
                            <button onClick={() => setActionModal({ isOpen: false, type: '', request: null })} className='btn btn-secondary'>
                                Cancel
                            </button>
                            <button
                                onClick={handleActionSubmit}
                                className={`btn disabled:opacity-60 disabled:cursor-not-allowed ${actionModal.type === 'reject' ? 'btn-danger' : 'btn-success'}`}
                                disabled={confirmLeaveRequest.isPending || approveLeaveRequest.isPending || rejectLeaveRequest.isPending}
                            >
                                {confirmLeaveRequest.isPending ? 'Confirming...'
                                    : approveLeaveRequest.isPending ? 'Approving...'
                                        : rejectLeaveRequest.isPending ? 'Rejecting...'
                                            : actionModal.type === 'confirm' ? 'Confirm'
                                                : actionModal.type === 'approve' ? 'Approve'
                                                    : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ViewLeaveDetailsModal
                isOpen={viewDetailsModal}
                onClose={() => setViewDetailsModal(false)}
                selectedLeave={selectedLeave}
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
            />

        </div>
    )
}
